import { Icon } from "@iconify/react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Select, { components } from "react-select";
import { toast } from "react-toastify";
import { Button, ErrorModal } from "../../components";
import { logoTagline, logoText, MAX_OTP_LENGTH, OTP_RESEND_INTERVAL } from "../../constants";
import { useAuth } from "../../hooks";
import { countryCodes } from "../../services/country-codes";
import styles from "./LoginPage.module.css";

const Option = (props) => {
  return (
    <components.Option {...props}>
      <div className="row">
        <Icon icon={props.data.flag} inline />
        <div className="full row">
          {props.data.label}
          <span className="small gray end"> ({props.data.value})</span>
        </div>
      </div>
    </components.Option>
  );
};

const SingleValue = (props) => {
  return (
    <components.SingleValue {...props}>
      {props.data.flag && props.data.flag !== "" ? (
        <Icon icon={props.data.flag} inline />
      ) : (
        <span className="small">{props.data.value}</span>
      )}
    </components.SingleValue>
  );
};

const LoginPage = () => {
  const [countryCode, setCountryCode] = useState({
    value: "+91",
    label: "India",
    countryCode: "IN",
    flag: "emojione:flag-for-india",
  }); // Default to India
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState(Array(MAX_OTP_LENGTH).fill("")); // Array to hold OTP digits
  const [isSendOtpDisabled, setIsSendOtpDisabled] = useState(true);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const { requestOtp, verifyOtp, validateAuth } = useAuth();
  const [showInvalidOTPError, setShowInvalidOTPError] = useState(false);
  const [invalidOTPErrorTimer, setInvalidOTPErrorTimer] = useState(0);

  // Handle Mobile Number input change
  const handleMobileNumberChange = (e) => {
    const value = e.target.value;
    setMobileNumber(value);
    setIsSendOtpDisabled(value.length !== 10 || isNaN(value)); // Disable Send OTP button if mobile number is invalid
  };

  // Handle OTP resend
  const handleResendOtp = () => {
    setOtp(Array(MAX_OTP_LENGTH).fill("")); // Clear OTP input
    handleSendOtp();
  };

  // Handle Send OTP button click
  const handleSendOtp = async () => {
    // const mobile = `${countryCode.value}${mobileNumber}`;
    const { success, error } = await requestOtp(mobileNumber);
    if (error) {
      toast.error(error);
      return;
    }

    toast.success(success);
    setIsOtpSent(true);
    setIsSendOtpDisabled(true);
    setOtpResendTimer(OTP_RESEND_INTERVAL);
    const timer = setInterval(() => {
      setOtpResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Handle OTP input change
  const handleOtpChange = (value, index) => {
    if (!/^[0-9]*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < MAX_OTP_LENGTH - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleOtpBackspace = (e, index) => {
    if (e.key === "Backspace" && index > 0 && otp[index] === "") {
      inputRefs.current[index - 1].focus();
    }
  };

  // Handle Submit/Login button click
  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== MAX_OTP_LENGTH) {
      toast.error("Please enter a valid OTP.");
      return;
    }
    localStorage.clear();

    const { error } = await verifyOtp(mobileNumber, otpValue);
    if (error) {
      setShowInvalidOTPError(true);
      setInvalidOTPErrorTimer(5);
      const timer = setInterval(() => {
        setInvalidOTPErrorTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setShowInvalidOTPError(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return;
    }

    navigate("/");
  };

  // Handle Invalid OTP error timer
  const handleCloseInvalidOTPError = () => {
    setShowInvalidOTPError(false);
    setInvalidOTPErrorTimer(0);
  };

  useEffect(() => {
    if (invalidOTPErrorTimer === 0) {
      setShowInvalidOTPError(false);
    }
  }, [invalidOTPErrorTimer]);

  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    const checkLoggedIn = async () => {
      const status = await validateAuth();
      setLoggedIn(status);
    };

    checkLoggedIn();
  }, []);

  useEffect(() => {
    if (loggedIn) navigate("/");
  }, [loggedIn]);

  return (
    <>
      <div className={styles.page}>
        <div className={styles.left}>
          <div className={styles.logo_wrapper}>
            <div className={styles.logo}>{logoText}</div>
            <div className={styles.logo_tagline}>{logoTagline}</div>
          </div>
        </div>
        <div className={styles.right}>
          <form className={styles.form_wrapper}>
            <h2 className={styles.title}>Login</h2>
            <div className={styles.column}>
              <div className={styles.label}>Mobile Number</div>
              <div className={styles.row}>
                <Select
                  options={countryCodes}
                  components={{ Option, SingleValue }}
                  styles={{
                    menu: (baseStyle) => ({ ...baseStyle, width: "fit-content" }),
                    indicatorSeparator: () => null,
                    control: (baseStyle) => ({
                      ...baseStyle,
                      borderRadius: "0.5rem",
                      border: "1px solid var(--gray-light)",
                      boxShadow: "none",
                      backgroundColor: "var(--gray-light-50)",
                      ":hover": {
                        border: "1px solid var(--gray-light)",
                      },
                    }),
                    input: (baseStyle) => ({ ...baseStyle, padding: "0.5rem 0" }),
                  }}
                  value={countryCode}
                  onChange={(e) => setCountryCode(e)}
                />
                <input
                  type="tel"
                  placeholder="Mobile number"
                  className={`full ${styles.input}`}
                  value={mobileNumber}
                  onChange={handleMobileNumberChange}
                  pattern="[0-9]{10}"
                  minLength={10}
                  maxLength={10}
                />
                <Button className="ghost" disabled={isSendOtpDisabled} onClick={handleSendOtp}>
                  Get OTP
                </Button>
              </div>
            </div>
            <div className={styles.column}>
              <div className={styles.label}>Enter OTP</div>
              <div className={styles.row}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    pattern="[0-9]{1}"
                    maxLength={1}
                    className={`${styles.otp_input} ${styles.input}`}
                    value={digit}
                    onChange={(e) => handleOtpChange(e.target.value, index)}
                    onKeyDown={(e) => handleOtpBackspace(e, index)}
                    ref={(ref) => (inputRefs.current[index] = ref)}
                    disabled={!isOtpSent}
                  />
                ))}
              </div>
              {isOtpSent ? (
                <div className={styles.row}>
                  <span>Didn't get the OTP?</span>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={otpResendTimer > 0}
                    className={styles.otp_resend}
                  >
                    {otpResendTimer > 0 ? <span className="gray">Resend in {otpResendTimer}s</span> : "Resend OTP"}
                  </button>
                </div>
              ) : (
                ""
              )}
            </div>
            <button type="submit" disabled={!isOtpSent} className={styles.submit} onClick={handleSubmit}>
              Login
            </button>
          </form>
        </div>
      </div>
      {showInvalidOTPError &&
        createPortal(
          <ErrorModal
            isVisible={showInvalidOTPError}
            handleClose={handleCloseInvalidOTPError}
            message="Invalid one-time password (OTP) Try again or generate a new otp."
          />,
          document.body
        )}
    </>
  );
};

export default LoginPage;
